import React, { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';
import { useRuntimeConfig } from '@/shared/hooks/useRuntimeConfig';
import type { RuntimeConfig } from '@/shared/types/runtimeConfig.types';
import { setSiteLocale } from '@/shared/utils/ssnUtils';

export interface RuntimeConfigContextType {
  config: RuntimeConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  getConfigValue: <T = string | number | boolean>(
    key: string,
    defaultValue?: T
  ) => T | undefined;
  isFeatureEnabled: (featureKey: string) => boolean;
}

const RuntimeConfigContext = createContext<RuntimeConfigContextType>({
  config: null,
  loading: true,
  error: null,
  refetch: () => {},
  getConfigValue: () => undefined,
  isFeatureEnabled: () => false,
});

interface RuntimeConfigProviderProps {
  children: ReactNode;
}

/**
 * Provider for runtime configuration
 * 
 * This provider fetches and makes available ~200 configuration parameters
 * that control feature toggles, business rules, and system settings.
 * 
 * It should be placed in the component tree after TenantProvider and AuthProvider
 * since it requires both tenant context and authentication.
 */
export const RuntimeConfigProvider: React.FC<RuntimeConfigProviderProps> = ({ children }) => {
  const runtimeConfigData = useRuntimeConfig();

  // Sync the site locale singleton used by the SSN masking utility.
  // This runs once the runtime config has finished loading so that
  // getObfuscatedSSN (called from the pure service layer) uses the
  // correct locale for masking decisions.
  useEffect(() => {
    if (!runtimeConfigData.loading && runtimeConfigData.config) {
      const locale = runtimeConfigData.getConfigValue<string>('general.locale');
      if (locale) {
        setSiteLocale(locale);
      }
 }
  }, [runtimeConfigData.loading, runtimeConfigData.config, runtimeConfigData.getConfigValue]);

  const contextValue = useMemo<RuntimeConfigContextType>(() => ({
    config: runtimeConfigData.config ?? null,
    loading: runtimeConfigData.loading,
    error: runtimeConfigData.error,
    refetch: runtimeConfigData.refetch,
    getConfigValue: runtimeConfigData.getConfigValue,
    isFeatureEnabled: runtimeConfigData.isFeatureEnabled,
  }), [runtimeConfigData.config, runtimeConfigData.loading, runtimeConfigData.error, runtimeConfigData.refetch, runtimeConfigData.getConfigValue, runtimeConfigData.isFeatureEnabled]);

  return (
    <RuntimeConfigContext.Provider value={contextValue}>
      {children}
    </RuntimeConfigContext.Provider>
  );
};

/**
 * Hook to access runtime configuration context
 * 
 * @throws Error if used outside of RuntimeConfigProvider
 */
export const useRuntimeConfigContext = () => {
  const context = useContext(RuntimeConfigContext);
  if (context === undefined) {
    throw new Error('useRuntimeConfigContext must be used within a RuntimeConfigProvider');
  }
  return context;
};